/**
 * Push notifications: the devices that asked for them, and what was sent.
 *
 * Two audiences. The browser (as the signed-in member) saves, reads and
 * removes its *own* device through `save_push_subscription()` and the
 * own-row policies; it can never see anybody else's. The sender script
 * (`scripts/send-notifications.js`, service role) reads every device, claims
 * a `notification_log` row before sending, and removes devices the push
 * service reports gone. See 20261002120000_push_notifications.sql, and
 * 20261007120000_take_notifications.sql for the per-take-event half.
 *
 * Every function takes the shared `ctx` ({ client, seasonsCache,
 * activeSeasonId }) as its first argument; see `./context.js`.
 */

import { DbErrorKind, throwDbError, toDbError, unwrap } from './errors.js';
import { createLogger } from './logger.js';
import { selectAll } from './paging.js';
import { getPickEmWeeklyBreakdown, getWeeklyPickEmScores } from './pickems.js';
import { getPlayoffBracketConfig, getPlayoffStandings } from './playoffs.js';
import { getUserDisplayNames } from './users.js';
import { WIN_KINDS, newestFinishedWeek } from '../notificationPlanner.js';
import { PICK_EM_TOURNEYS } from '../../src/components/pickems/tourneys.js';
import { ownerKey } from '../../utils/ownerAliases.js';

const log = createLogger('db:notifications');

// ---------------------------------------------------------------------------
// The member's own device
// ---------------------------------------------------------------------------

/**
 * Store this device's subscription with the topics it wants. `subscription` is
 * the browser's `PushSubscription` (or its `toJSON()`); returns the stored
 * topics.
 */
export async function saveMyPushSubscription(ctx, subscription, topics, userAgent = null) {
  const json = typeof subscription?.toJSON === 'function' ? subscription.toJSON() : subscription;
  const endpoint = json?.endpoint;
  const keys = json?.keys ?? {};

  if (!endpoint || !keys.p256dh || !keys.auth) {
    throwDbError(new Error('The browser returned an incomplete push subscription'), 'Save push subscription');
  }

  const result = await ctx.client.rpc('save_push_subscription', {
    p_endpoint: endpoint,
    p_p256dh: keys.p256dh,
    p_auth: keys.auth,
    p_topics: topics,
    p_user_agent: userAgent
  });
  return unwrap(result, 'Save push subscription') ?? [];
}

/** This device's stored topics, or null when the server has no row for it. */
export async function getMyPushSubscription(ctx, endpoint) {
  if (!endpoint) return null;
  const result = await ctx.client
    .from('push_subscriptions')
    .select('topics')
    .eq('endpoint', endpoint)
    .maybeSingle();
  const row = unwrap(result, 'Get push subscription', { allowMissing: true });
  return row ? { topics: row.topics ?? [] } : null;
}

/** Forget this device. RLS limits the delete to the caller's own rows. */
export async function deleteMyPushSubscription(ctx, endpoint) {
  if (!endpoint) return;
  unwrap(
    await ctx.client.from('push_subscriptions').delete().eq('endpoint', endpoint),
    'Delete push subscription'
  );
}

// ---------------------------------------------------------------------------
// The member's alerts (the Alerts page, /alerts)
// ---------------------------------------------------------------------------

/** How many alerts the tab and the cog read: the newest, which is all anybody scrolls to. */
export const INBOX_LIMIT = 100;

/**
 * The signed-in member's alerts, newest first. RLS limits the read to their
 * own rows; see 20261014120000_notification_inbox.sql.
 */
export async function getMyInbox(ctx, { limit = INBOX_LIMIT } = {}) {
  const rows = unwrap(
    await ctx.client
      .from('notification_inbox')
      .select('id, kind, title, body, url, created_at, read_at')
      .order('created_at', { ascending: false })
      .limit(limit),
    'Get alerts'
  ) ?? [];
  return rows.map((row) => ({
    id: row.id,
    kind: row.kind,
    title: row.title,
    body: row.body ?? '',
    url: row.url ?? null,
    createdAt: new Date(row.created_at),
    readAt: row.read_at ? new Date(row.read_at) : null
  }));
}

/**
 * Mark alerts read: these ids, or every unread one with no ids. The database
 * stamps the time. Returns how many changed.
 */
export async function markInboxRead(ctx, ids = null) {
  const result = await ctx.client.rpc('mark_notifications_read', { p_ids: ids?.length ? ids : null });
  return unwrap(result, 'Mark alerts read') ?? 0;
}

/** Delete one of the member's own alerts. */
export async function deleteInboxItem(ctx, id) {
  if (!id) return;
  unwrap(await ctx.client.from('notification_inbox').delete().eq('id', id), 'Delete alert');
}

// ---------------------------------------------------------------------------
// The sender (service role)
// ---------------------------------------------------------------------------

/** Every stored device, for the sender. */
export async function getPushSubscriptions(ctx) {
  try {
    const rows = await selectAll(() => ctx.client
      .from('push_subscriptions')
      .select('id, user_id, endpoint, p256dh, auth, topics')
      .order('id'));
    return rows.map((row) => ({
      id: row.id,
      userId: row.user_id,
      endpoint: row.endpoint,
      keys: { p256dh: row.p256dh, auth: row.auth },
      topics: row.topics ?? []
    }));
  } catch (error) {
    throwDbError(error, 'Get push subscriptions');
  }
}

/** The kinds already sent for one week. */
export async function getSentNotificationKinds(ctx, seasonId, week) {
  const rows = unwrap(
    await ctx.client
      .from('notification_log')
      .select('kind')
      .eq('season_id', seasonId)
      .eq('week', week),
    'Get notification log'
  ) ?? [];
  return new Set(rows.map((row) => row.kind));
}

/**
 * Claim a (kind, season, week) before sending. Returns the log row's id, or
 * null when another run already claimed it — the unique key is what makes a
 * re-run or a race send nothing twice.
 */
export async function claimNotification(ctx, { kind, seasonId, week, recipients }) {
  const { data, error } = await ctx.client
    .from('notification_log')
    .insert({ kind, season_id: seasonId, week, recipients })
    .select('id')
    .single();

  if (error) {
    const dbError = toDbError(error, 'Claim notification');
    if (dbError.kind === DbErrorKind.DUPLICATE) {
      log.info(`${kind} for week ${week} was already sent; skipping`);
      return null;
    }
    throw dbError;
  }
  return data.id;
}

/** Record how a claimed send went. */
export async function finishNotification(ctx, id, { delivered, failed, removed }) {
  unwrap(
    await ctx.client.from('notification_log').update({ delivered, failed, removed }).eq('id', id),
    'Finish notification'
  );
}

/**
 * Keep what was sent, one row per member, for Alerts. `rows` are
 * `{ userId, kind, title, body, url }`, from `inboxRows` in the sender.
 */
export async function addToInbox(ctx, rows) {
  if (!rows?.length) return 0;
  unwrap(
    await ctx.client.from('notification_inbox').insert(rows.map((row) => ({
      user_id: row.userId,
      kind: row.kind,
      title: row.title,
      body: row.body ?? '',
      url: row.url ?? null
    }))),
    'Add to alerts'
  );
  return rows.length;
}

/** Drop devices the push service says are gone (404/410). */
export async function deletePushSubscriptions(ctx, endpoints) {
  if (!endpoints?.length) return;
  unwrap(
    await ctx.client.from('push_subscriptions').delete().in('endpoint', endpoints),
    'Delete push subscriptions'
  );
}

/** Stamp the devices a send reached. */
export async function markPushSubscriptionsSent(ctx, endpoints, at = new Date()) {
  if (!endpoints?.length) return;
  unwrap(
    await ctx.client
      .from('push_subscriptions')
      .update({ last_sent_at: at.toISOString() })
      .in('endpoint', endpoints),
    'Mark push subscriptions sent'
  );
}

/** Members with at least one pick'em pick saved for a week. */
export async function getPickEmSubmitterIds(ctx, pickEmWeekId) {
  try {
    const rows = await selectAll(() => ctx.client
      .from('pick_em_submissions')
      .select('id, user_id')
      .eq('pick_em_week_id', pickEmWeekId)
      .order('id'));
    return new Set(rows.map((row) => row.user_id));
  } catch (error) {
    throwDbError(error, 'Get pick em submitters');
  }
}

/**
 * The active season and the pick'em week whose window contains `now`, or
 * nulls. Read by window rather than by derived week number, so a week the
 * admin opened early or a window the season's settings moved is still found.
 */
export async function getOpenPickEmWeek(ctx, now = new Date()) {
  const season = unwrap(
    await ctx.client.from('v_active_season').select('id, year, timezone').maybeSingle(),
    'Get active season',
    { allowMissing: true }
  );
  if (!season) return { season: null, week: null };

  const iso = now.toISOString();
  const row = unwrap(
    await ctx.client
      .from('pick_em_weeks')
      .select('id, season_id, week_number, submission_opens_at, submission_closes_at')
      .eq('season_id', season.id)
      .lte('submission_opens_at', iso)
      .gt('submission_closes_at', iso)
      .order('week_number', { ascending: false })
      .limit(1)
      .maybeSingle(),
    'Get open pick em week',
    { allowMissing: true }
  );

  return {
    season: { id: season.id, year: season.year, timeZone: season.timezone || 'America/New_York' },
    week: row
      ? {
          id: row.id,
          seasonId: row.season_id,
          weekNumber: row.week_number,
          opensAt: new Date(row.submission_opens_at),
          closesAt: new Date(row.submission_closes_at)
        }
      : null
  };
}

// ---------------------------------------------------------------------------
// Winners (service role)
// ---------------------------------------------------------------------------

/**
 * Everything `planWinnerNotifications` needs: the result of the newest finished
 * week, the tourney that week ended, the bracket challenge if the final is over
 * and the admin has released it, and what notification_log already holds.
 *
 * A tourney ends with its last week whether or not that week had a pick'em,
 * because the sync does not open one in the playoffs: the second half's picks
 * can stop at week 14 and it is still decided when week 17 is.
 */
export async function getWinnerNotificationInputs(ctx) {
  const season = unwrap(
    await ctx.client.from('v_active_season').select('id, year').maybeSingle(),
    'Get active season',
    { allowMissing: true }
  );
  if (!season) return { season: null };

  const games = await selectAll(() => ctx.client
    .from('games')
    .select('id, week, type, team2_id, is_completed')
    .eq('season_id', season.id)
    .order('id'))
    .then((rows) => rows.map((row) => ({
      week: row.week,
      type: row.type,
      team2Id: row.team2_id,
      isCompleted: Boolean(row.is_completed)
    })))
    .catch((error) => throwDbError(error, 'Get games for winners'));

  const week = newestFinishedWeek(games);
  const unapproved = await ctx.client.from('member_approvals').select('user_id').neq('status', 'approved')
    .then((result) => unwrap(result, 'Get unapproved members') ?? []);
  const inputs = {
    season,
    week,
    weekly: null,
    tourney: null,
    bracket: null,
    sent: new Set(),
    excludedUserIds: new Set(unapproved.map((row) => row.user_id))
  };

  const noteSent = async (weekNumber) => {
    const kinds = await getSentNotificationKinds(ctx, season.id, weekNumber);
    for (const kind of kinds) inputs.sent.add(`${kind}:${weekNumber}`);
  };

  if (week != null) {
    await noteSent(week);

    const pickEmWeek = unwrap(
      await ctx.client
        .from('pick_em_weeks')
        .select('id')
        .eq('season_id', season.id)
        .eq('week_number', week)
        .maybeSingle(),
      'Get pick em week for winners',
      { allowMissing: true }
    );
    if (pickEmWeek && !inputs.sent.has(`${WIN_KINDS.week}:${week}`)) {
      inputs.weekly = { week, scores: await getWeeklyPickEmScores(ctx, pickEmWeek.id) };
    }

    const tourney = PICK_EM_TOURNEYS.find((candidate) => candidate.endWeek === week);
    if (tourney && !inputs.sent.has(`${WIN_KINDS.tourney(tourney.id)}:${week}`)) {
      inputs.tourney = { tourney, weeklyScores: await getPickEmWeeklyBreakdown(ctx, season.id) };
    }
  }

  const final = games.find((game) => game.type === 'playoff_championship');
  if (final?.isCompleted) {
    await noteSent(final.week);
    const config = await getPlayoffBracketConfig(ctx, season.id);
    if (config?.resultsReleased && !inputs.sent.has(`${WIN_KINDS.bracket}:${final.week}`)) {
      inputs.bracket = { week: final.week, standings: await getPlayoffStandings(ctx, season.id) };
    }
  }

  return inputs;
}

// ---------------------------------------------------------------------------
// Takes (service role)
// ---------------------------------------------------------------------------

/**
 * The take events that can be announced. Withdrawals and grades are not, and
 * of edits only the author's own: `planTakeNotifications` reads `actedAsAdmin`.
 */
export const ANNOUNCED_TAKE_EVENTS = Object.freeze(['posted', 'edited', 'faded', 'backed']);

/**
 * Everything `planTakeNotifications` needs, for events since `since`: the
 * events, which (event, kind) pairs notification_log already holds, their
 * takes, those takes' current participants and answers to staked Hell Yeahs,
 * the names involved, and the accounts that may not read takes.
 *
 * "Not yet sent" is read from notification_log rather than marked on the
 * event, because `take_events` is append-only and no client can write it. It
 * is per kind, not per event: a staked Hell Yeah tells the author and the
 * Hell Nahs separately, and one being sent says nothing about the other.
 */
export async function getTakeNotificationInputs(ctx, since) {
  const sinceIso = since.toISOString();

  const [eventRows, claimedRows] = await Promise.all([
    selectAll(() => ctx.client
      .from('take_events')
      .select('id, seq, take_id, season_id, event_type, subject_id, acted_as_admin, created_at')
      .in('event_type', ANNOUNCED_TAKE_EVENTS)
      .gte('created_at', sinceIso)
      .order('seq')).catch((error) => throwDbError(error, 'Get take events')),
    // Sent after the event, so anything claimed for these events was sent
    // since `since` too.
    selectAll(() => ctx.client
      .from('notification_log')
      .select('id, take_event_id, kind')
      .not('take_event_id', 'is', null)
      .gte('sent_at', sinceIso)
      .order('id')).catch((error) => throwDbError(error, 'Get take notification log'))
  ]);

  const claimed = new Set(claimedRows.map((row) => `${row.take_event_id}:${row.kind}`));
  const events = eventRows
    .map((row) => ({
      id: row.id,
      takeId: row.take_id,
      seasonId: row.season_id,
      eventType: row.event_type,
      subjectId: row.subject_id,
      actedAsAdmin: Boolean(row.acted_as_admin),
      createdAt: new Date(row.created_at)
    }));

  if (!events.length) {
    return { events, claimed, takes: new Map(), participants: [], stakeResponses: [], displayNames: {}, excludedUserIds: new Set() };
  }

  const takeIds = [...new Set(events.map((event) => event.takeId))];
  const [takeRows, participantRows, responseRows, unapprovedRows] = await Promise.all([
    ctx.client.from('takes').select('id, user_id, body, wager').in('id', takeIds)
      .then((result) => unwrap(result, 'Get takes for notifications') ?? []),
    ctx.client.from('take_participants').select('id, take_id, user_id, side, wager').in('take_id', takeIds)
      .then((result) => unwrap(result, 'Get take participants for notifications') ?? []),
    ctx.client.from('take_stake_responses').select('hell_yeah_id, user_id, response').in('take_id', takeIds)
      .then((result) => unwrap(result, 'Get stake answers for notifications') ?? []),
    // The admin has no row and is approved; every other account has one.
    ctx.client.from('member_approvals').select('user_id').neq('status', 'approved')
      .then((result) => unwrap(result, 'Get unapproved members') ?? [])
  ]);

  const takes = new Map(takeRows.map((row) => [row.id, {
    id: row.id,
    authorId: row.user_id,
    body: row.body,
    wager: row.wager ?? null
  }]));
  const participants = participantRows.map((row) => ({
    id: row.id,
    takeId: row.take_id,
    userId: row.user_id,
    side: row.side ?? 'nah',
    wager: row.wager ?? null
  }));

  const nameIds = new Set();
  for (const take of takes.values()) nameIds.add(take.authorId);
  for (const event of events) if (event.subjectId) nameIds.add(event.subjectId);
  const displayNames = await getUserDisplayNames(ctx, [...nameIds]);

  const stakeResponses = responseRows.map((row) => ({
    hellYeahId: row.hell_yeah_id,
    userId: row.user_id,
    response: row.response
  }));

  return {
    events,
    claimed,
    takes,
    participants,
    stakeResponses,
    displayNames,
    excludedUserIds: new Set(unapprovedRows.map((row) => row.user_id))
  };
}

/**
 * Claim one take event, for one audience (`kind`), before sending it. Returns
 * the log row's id, or null when another run already claimed that pair.
 */
export async function claimTakeNotification(ctx, { kind, seasonId, takeEventId, recipients }) {
  const { data, error } = await ctx.client
    .from('notification_log')
    .insert({ kind, season_id: seasonId, take_event_id: takeEventId, recipients })
    .select('id')
    .single();

  if (error) {
    const dbError = toDbError(error, 'Claim take notification');
    if (dbError.kind === DbErrorKind.DUPLICATE) {
      log.info(`take event ${takeEventId} (${kind}) was already sent; skipping`);
      return null;
    }
    throw dbError;
  }
  return data.id;
}

// ---------------------------------------------------------------------------
// Matchup facts (service role)
// ---------------------------------------------------------------------------

/** The active season, as `matchupDay` reads it, or null. */
export async function getMatchupFactSeason(ctx) {
  const row = unwrap(
    await ctx.client
      .from('v_active_season')
      .select('id, year, start_date, timezone, week_count')
      .maybeSingle(),
    'Get active season',
    { allowMissing: true }
  );
  if (!row) return null;
  return {
    id: row.id,
    year: row.year,
    startDate: row.start_date,
    weekCount: row.week_count,
    timeZone: row.timezone || 'America/New_York'
  };
}

/**
 * Everything `planMatchupFactNotifications` needs besides the devices: the
 * league's whole history (seasons, teams, every scored game), this week's
 * pairings, which team each member owns, and the accounts that are not
 * approved members.
 *
 * A member's team is their display name against `teams.owner` — the same
 * comparison that unmasks the league for them (`ownerKey`). `teams.user_id` is
 * not an owner: the `set_user_id` trigger stamps whoever inserted the row, so
 * today it is the admin on every team. It is used only as a fallback, and only
 * when it names exactly one team in the season.
 */
export async function getMatchupFactInputs(ctx, { season, week, userIds = [] }) {
  const [seasonRows, teamRows, results, gameRows, unapprovedRows, lineupRows, playerRows, eventRows] = await Promise.all([
    ctx.client.from('seasons').select('id, year, is_completed')
      .then((result) => unwrap(result, 'Get seasons') ?? []),
    selectAll(() => ctx.client
      .from('teams')
      .select('id, season_id, franchise_id, owner, user_id, made_playoffs, playoff_finish, final_rank')
      .order('id')).catch((error) => throwDbError(error, 'Get teams for matchup facts')),
    // One row per team per scored game; passes 1,000 rows, so paged.
    selectAll(() => ctx.client
      .from('v_game_results')
      .select('game_id, season_id, week, type, is_regular, is_playoff, is_consolation, team_id, opponent_id, points_for, points_against, result')
      .order('game_id')
      .order('team_id')).catch((error) => throwDbError(error, 'Get game results for matchup facts')),
    // This week's games, scored or not; v_game_results holds scored games only.
    ctx.client.from('games').select('team1_id, team2_id').eq('season_id', season.id).eq('week', week)
      .then((result) => unwrap(result, 'Get this week\'s games') ?? []),
    ctx.client.from('member_approvals').select('user_id').neq('status', 'approved')
      .then((result) => unwrap(result, 'Get unapproved members') ?? []),
    // Every settled team-week since 2020.
    selectAll(() => ctx.client
      .from('team_week_lineups')
      .select('id, season_id, week, team_id, starter_points, optimal_points, bench_points')
      .order('id')).catch((error) => throwDbError(error, 'Get lineups for matchup facts')),
    // This season's player weeks, for the player facts.
    selectAll(() => ctx.client
      .from('player_week_stats')
      .select('id, week, team_id, player_id, espn_player_id, started, actual_points')
      .eq('season_id', season.id)
      .order('id')).catch((error) => throwDbError(error, 'Get player weeks for matchup facts')),
    // This season's adds, for the pickup facts.
    selectAll(() => ctx.client
      .from('transaction_events')
      .select('id, type, scoring_period, espn_player_ids, player_from_franchise_ids, player_to_franchise_ids')
      .eq('season_id', season.id)
      .in('type', ['WAIVER', 'FREEAGENT'])
      .order('id')).catch((error) => throwDbError(error, 'Get pickups for matchup facts'))
  ]);

  const playerIds = [...new Set(playerRows.map((row) => row.player_id).filter(Boolean))];
  const playerNames = new Map();
  for (let i = 0; i < playerIds.length; i += 150) {
    const rows = unwrap(
      await ctx.client.from('players').select('id, name').in('id', playerIds.slice(i, i + 150)),
      'Get player names for matchup facts'
    ) ?? [];
    for (const row of rows) playerNames.set(row.id, row.name);
  }

  const teams = teamRows.map((row) => ({
    id: row.id,
    seasonId: row.season_id,
    franchiseId: row.franchise_id,
    owner: row.owner ?? '',
    userId: row.user_id,
    madePlayoffs: row.made_playoffs,
    playoffFinish: row.playoff_finish,
    finalRank: row.final_rank
  }));

  const pairings = gameRows
    .filter((row) => row.team1_id && row.team2_id && row.team1_id !== row.team2_id)
    .flatMap((row) => [
      { teamId: row.team1_id, opponentId: row.team2_id },
      { teamId: row.team2_id, opponentId: row.team1_id }
    ]);

  const thisSeason = teams.filter((team) => team.seasonId === season.id);
  // Who could be taunted: a team counts only when an approved account owns it.
  const teamAccounts = await getTeamAccounts(ctx, thisSeason);
  const memberTeams = new Map();
  const members = [...new Set(userIds)];
  const names = members.length ? await getUserDisplayNames(ctx, members) : {};
  for (const userId of members) {
    const key = ownerKey(names[userId]);
    const byName = key ? thisSeason.filter((t) => ownerKey(t.owner) === key) : [];
    const byCreator = thisSeason.filter((t) => t.userId === userId);
    const team = byName.length === 1 ? byName[0] : byCreator.length === 1 ? byCreator[0] : null;
    if (team) memberTeams.set(userId, team.id);
  }

  return {
    seasons: seasonRows.map((row) => ({ id: row.id, year: row.year, isCompleted: Boolean(row.is_completed) })),
    teams,
    teamAccounts,
    results: results.map((row) => ({
      seasonId: row.season_id,
      week: row.week,
      type: row.type,
      isRegular: Boolean(row.is_regular),
      isPlayoff: Boolean(row.is_playoff),
      isConsolation: Boolean(row.is_consolation),
      teamId: row.team_id,
      opponentId: row.opponent_id,
      pointsFor: row.points_for == null ? null : Number(row.points_for),
      pointsAgainst: row.points_against == null ? null : Number(row.points_against),
      result: row.result
    })),
    lineups: lineupRows.map((row) => ({
      seasonId: row.season_id,
      week: row.week,
      teamId: row.team_id,
      starterPoints: Number(row.starter_points),
      optimalPoints: Number(row.optimal_points),
      benchPoints: Number(row.bench_points)
    })),
    playerWeeks: playerRows.map((row) => ({
      week: row.week,
      teamId: row.team_id,
      espnPlayerId: row.espn_player_id,
      name: playerNames.get(row.player_id) ?? null,
      started: Boolean(row.started),
      points: row.actual_points == null ? null : Number(row.actual_points)
    })),
    pickups: eventRows.map((row) => ({
      type: row.type,
      week: row.scoring_period,
      espnPlayerIds: row.espn_player_ids ?? [],
      from: row.player_from_franchise_ids ?? [],
      to: row.player_to_franchise_ids ?? []
    })),
    pairings,
    memberTeams,
    excludedUserIds: new Set(unapprovedRows.map((row) => row.user_id))
  };
}

/**
 * Every matchup fact these members have been sent, by user id, for
 * `pickFact`'s never-repeat rule.
 */
export async function getMatchupFactHistory(ctx, userIds) {
  const history = new Map();
  if (!userIds?.length) return history;
  const rows = await selectAll(() => ctx.client
    .from('matchup_fact_log')
    .select('id, user_id, fact, family, subject, sent_at')
    .in('user_id', [...new Set(userIds)])
    .order('id')).catch((error) => throwDbError(error, 'Get matchup fact history'));
  for (const row of rows) {
    if (!history.has(row.user_id)) history.set(row.user_id, []);
    history.get(row.user_id).push({
      factKey: row.fact,
      family: row.family,
      subject: row.subject,
      sentAt: new Date(row.sent_at)
    });
  }
  return history;
}

/** Record the facts sent today, one row per member. A day already recorded is left alone. */
export async function recordMatchupFacts(ctx, rows) {
  if (!rows?.length) return;
  unwrap(
    await ctx.client.from('matchup_fact_log').upsert(
      rows.map((row) => ({
        user_id: row.userId,
        season_id: row.seasonId,
        week: row.week,
        day: row.day,
        subject: row.subject,
        family: row.family,
        fact: row.fact,
        sent_at: row.sentAt.toISOString()
      })),
      { onConflict: 'user_id,season_id,week,day', ignoreDuplicates: true }
    ),
    'Record matchup facts'
  );
}

// ---------------------------------------------------------------------------
// Taunts (20261020120000_matchup_taunts.sql)
// ---------------------------------------------------------------------------

/**
 * The approved accounts that own each of these teams, by team id: a member's
 * display name against `teams.owner` (`ownerKey`), the matchup facts' rule.
 * Admins are approved without a `member_approvals` row, so they are read from
 * `league_admins` too. A team nobody has claimed is absent.
 */
export async function getTeamAccounts(ctx, teams) {
  const [approved, admins] = await Promise.all([
    ctx.client.from('member_approvals').select('user_id').eq('status', 'approved')
      .then((result) => unwrap(result, 'Get approved members') ?? []),
    ctx.client.from('league_admins').select('user_id')
      .then((result) => unwrap(result, 'Get league admins') ?? [])
  ]);
  const ids = [...new Set([...approved, ...admins].map((row) => row.user_id))];
  const names = ids.length ? await getUserDisplayNames(ctx, ids) : {};

  const byOwner = new Map();
  for (const id of ids) {
    const key = ownerKey(names[id]);
    if (!key) continue;
    if (!byOwner.has(key)) byOwner.set(key, []);
    byOwner.get(key).push(id);
  }

  const accounts = new Map();
  for (const team of teams) {
    const users = byOwner.get(ownerKey(team.owner));
    if (users?.length) accounts.set(team.id, users);
  }
  return accounts;
}

/**
 * Store today's taunt offers, one per member, and return their ids by user id.
 * A member already offered one today keeps that row; if it has not been sent
 * its wording is brought up to date (a test send and the noon send can pick
 * different facts), and if it has been sent it is left exactly as it went.
 * Every offer in one call shares a season, week and day — one plan.
 */
export async function saveTauntOffers(ctx, offers) {
  const ids = new Map();
  if (!offers?.length) return ids;
  const { seasonId, week, day } = offers[0];

  const existing = unwrap(
    await ctx.client
      .from('matchup_taunts')
      .select('id, from_user_id, sent_at')
      .eq('season_id', seasonId)
      .eq('week', week)
      .eq('day', day)
      .in('from_user_id', offers.map((offer) => offer.userId)),
    'Get taunt offers'
  ) ?? [];
  const byUser = new Map(existing.map((row) => [row.from_user_id, row]));

  const columns = (offer) => ({
    to_team_id: offer.toTeamId,
    sender_name: offer.senderName,
    opponent_name: offer.opponentName,
    fact: offer.fact,
    taunt: offer.taunt
  });

  const fresh = offers.filter((offer) => !byUser.has(offer.userId));
  if (fresh.length) {
    const inserted = unwrap(
      await ctx.client
        .from('matchup_taunts')
        .insert(fresh.map((offer) => ({
          from_user_id: offer.userId,
          season_id: seasonId,
          week,
          day,
          ...columns(offer)
        })))
        .select('id, from_user_id'),
      'Save taunt offers'
    ) ?? [];
    for (const row of inserted) ids.set(row.from_user_id, row.id);
  }

  for (const offer of offers) {
    const row = byUser.get(offer.userId);
    if (!row) continue;
    ids.set(offer.userId, row.id);
    if (row.sent_at) continue;
    unwrap(
      await ctx.client.from('matchup_taunts').update(columns(offer)).eq('id', row.id).is('sent_at', null),
      'Refresh taunt offer'
    );
  }
  return ids;
}

/** Taunts pressed since `since` and not yet delivered, oldest first. */
export async function getPendingTaunts(ctx, since) {
  const rows = unwrap(
    await ctx.client
      .from('matchup_taunts')
      .select('id, from_user_id, to_team_id, sender_name, opponent_name, taunt, sent_at')
      .gte('sent_at', since.toISOString())
      .is('delivered_at', null)
      .order('sent_at'),
    'Get pending taunts'
  ) ?? [];
  return rows.map((row) => ({
    id: row.id,
    fromUserId: row.from_user_id,
    toTeamId: row.to_team_id,
    senderName: row.sender_name,
    opponentName: row.opponent_name,
    taunt: row.taunt,
    sentAt: new Date(row.sent_at)
  }));
}

/** The teams these taunts are aimed at, for their owners. */
export async function getTeamsById(ctx, teamIds) {
  if (!teamIds?.length) return [];
  return unwrap(
    await ctx.client.from('teams').select('id, owner').in('id', [...new Set(teamIds)]),
    'Get taunted teams'
  ) ?? [];
}

/**
 * Claim a taunt's delivery before sending it. Returns false when another run
 * already did, which is what makes a re-run or a race deliver it once.
 */
export async function claimTaunt(ctx, id) {
  const rows = unwrap(
    await ctx.client
      .from('matchup_taunts')
      .update({ delivered_at: new Date().toISOString() })
      .eq('id', id)
      .is('delivered_at', null)
      .select('id'),
    'Claim taunt'
  ) ?? [];
  return rows.length > 0;
}

// ---------------------------------------------------------------------------
// The member's own taunts (the Alerts page)
// ---------------------------------------------------------------------------

/** The signed-in member's taunt offers, newest first. RLS limits it to their own. */
export async function getMyTaunts(ctx, { limit = INBOX_LIMIT } = {}) {
  const rows = unwrap(
    await ctx.client
      .from('matchup_taunts')
      .select('id, opponent_name, fact, taunt, created_at, sent_at')
      .order('created_at', { ascending: false })
      .limit(limit),
    'Get taunts'
  ) ?? [];
  return rows.map((row) => ({
    id: row.id,
    opponentName: row.opponent_name,
    fact: row.fact,
    taunt: row.taunt,
    createdAt: new Date(row.created_at),
    sentAt: row.sent_at ? new Date(row.sent_at) : null
  }));
}

/** Taunt with one offer. Resolves to 'sent', 'already_sent', 'expired' or 'not_found'. */
export async function sendTaunt(ctx, id) {
  return unwrap(await ctx.client.rpc('send_taunt', { p_id: id }), 'Send taunt');
}
